import { type LessonSummary } from '@/platform/learning/contract'
import { type LearningService } from '@/platform/learning/service'

import { useFocusOnMount } from './focus'
import { type Subject, SUBJECTS } from './subjects'
import { TodayLesson } from './TodayLesson'

/**
 * Child Home (UI v0.2 §4.2, Slice 1 product shell): a welcome, today's lesson,
 * the four P0 subjects (D-14.2) and the school-materials entry point (D-14.3).
 * Only English and today's lesson lead to real learning; the other subjects
 * open honest "coming soon" pages, and school materials open the Slice 2
 * journey (local preview, DEMO samples). No badges, streaks or scores.
 */

export interface ChildHomeProps {
  readonly learning: LearningService
  readonly learnerId: string
  readonly nickname: string
  /** The lesson the child left mid-session, if any: offers to continue it. */
  readonly inProgressLessonId: string | null
  readonly starting: boolean
  readonly startFailed: boolean
  readonly onStart: (lesson: LessonSummary) => void
  readonly onOpenSubject: (subject: Subject) => void
  readonly onOpenSchoolMaterials: () => void
  /** Read Aloud entry; absent where the family may not use it. */
  readonly onOpenReadAloud?: () => void
  /** DEMO school materials can be tried (local/development builds only). */
  readonly samplesAvailable?: boolean
  /**
   * Take focus when shown. True when the child navigates back here; false on
   * the first load of child mode, where nothing should be focused yet.
   */
  readonly focusGreeting: boolean
}

export const SUBJECTS_DEMO_NOTE =
  'Toán, Vật lý và Hóa học chưa có bài học riêng. Con có thể thử cả bốn môn với tài liệu mẫu (DEMO) ở mục “Học từ tài liệu ở trường”.'

const CARD =
  'flex w-full items-center gap-3 rounded-2xl border-2 border-slate-200 bg-white p-4 text-left active:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800'

export function ChildHome({
  learning,
  learnerId,
  nickname,
  inProgressLessonId,
  starting,
  startFailed,
  onStart,
  onOpenSubject,
  onOpenSchoolMaterials,
  onOpenReadAloud,
  samplesAvailable = false,
  focusGreeting,
}: ChildHomeProps): React.JSX.Element {
  const greeting = useFocusOnMount<HTMLHeadingElement>(focusGreeting)

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-8 px-5 pt-2 pb-16 md:max-w-3xl">
      <section
        aria-labelledby="child-greeting"
        className="flex flex-col items-center gap-2 text-center"
      >
        <p className="text-6xl" aria-hidden>
          🌟
        </p>
        <h1
          id="child-greeting"
          ref={greeting}
          tabIndex={-1}
          className="text-3xl font-bold"
        >
          Chào {nickname}!
        </h1>
        <p className="text-lg text-slate-700 dark:text-slate-300">
          Hôm nay mình cùng học nhé.
        </p>
      </section>

      <div className="flex flex-col items-center gap-3 text-center">
        <TodayLesson
          learning={learning}
          learnerId={learnerId}
          inProgressLessonId={inProgressLessonId}
          starting={starting}
          startFailed={startFailed}
          onStart={onStart}
        />
      </div>

      <section aria-labelledby="child-subjects" className="flex flex-col gap-3">
        <h2 id="child-subjects" className="text-xl font-bold">
          Môn học
        </h2>
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {SUBJECTS.map((subject) => (
            <li key={subject.code}>
              <button
                type="button"
                className={`${CARD} h-full flex-col items-start`}
                onClick={() => {
                  onOpenSubject(subject)
                }}
              >
                <span className="text-3xl" aria-hidden>
                  {subject.icon}
                </span>
                <span className="text-lg font-semibold">{subject.name}</span>
                <span className="text-base text-slate-600 dark:text-slate-400">
                  {subject.available ? 'Vào học' : 'Sắp có'}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {samplesAvailable && (
          <p className="text-base text-slate-700 dark:text-slate-300">
            {SUBJECTS_DEMO_NOTE}
          </p>
        )}
      </section>

      <section aria-labelledby="child-materials" className="flex flex-col gap-3">
        <h2 id="child-materials" className="text-xl font-bold">
          Học từ tài liệu ở trường
        </h2>
        <button type="button" className={CARD} onClick={onOpenSchoolMaterials}>
          <span className="text-3xl" aria-hidden>
            📷
          </span>
          <span className="flex flex-col">
            <span className="text-lg font-semibold">Tài liệu ở trường</span>
            <span className="text-base text-slate-600 dark:text-slate-400">
              Học từ ảnh bài tập, trang sách hoặc PDF
            </span>
          </span>
        </button>
      </section>

      {onOpenReadAloud !== undefined && (
        <section aria-labelledby="child-read-aloud" className="flex flex-col gap-3">
          <h2 id="child-read-aloud" className="text-xl font-bold">
            Luyện đọc tiếng Anh
          </h2>
          <button type="button" className={CARD} onClick={onOpenReadAloud}>
            <span className="text-3xl" aria-hidden>
              🎤
            </span>
            <span className="flex flex-col">
              <span className="text-lg font-semibold">Đọc to</span>
              <span className="text-base text-slate-600 dark:text-slate-400">
                Đọc một câu tiếng Anh và luyện những từ khó
              </span>
            </span>
          </button>
        </section>
      )}
    </div>
  )
}
